import { Platform, type GestureResponderEvent } from "@-/com.npmjs.react-native";
import { useRef } from "react";
import { DEFAULT_DELAY_LONG_PRESS_MS } from "#default-delay-long-press-ms";

/**
 * Whether a release ends a hold rather than a tap, judged by the touch's native timestamps.
 *
 * Those are stamped when the finger went down and came up, not when JS heard about it, so a
 * release that reached JS late behind a UI-thread stall still measures as the short tap it was.
 */
export function isHoldRelease(
  pressInTimestamp: number | undefined,
  releaseTimestamp: number | undefined,
  holdMs: number = DEFAULT_DELAY_LONG_PRESS_MS,
): boolean {
  if (pressInTimestamp === undefined || releaseTimestamp === undefined) {
    return false;
  }
  return releaseTimestamp - pressInTimestamp >= holdMs;
}

/**
 * Press props for `selectable` text that also does something on tap (expands its definitions).
 *
 * Holding the text selects it natively, and that hold's release must not tap too. A no-op
 * `onLongPress` would suppress it, but only through `Pressable`'s JS timer, which also swallows a
 * tap whose release is late behind a stall. This classifies the release by the touch's own
 * timestamps instead. Web text taps are clicks, which a hold-to-select never produces.
 *
 * Without `onPress` the text still claims its touches, as the no-op `onLongPress` made it do, so a
 * hold that selects it never falls through to a pressable row behind it.
 */
export function useSelectableTextPress(onPress: SelectableTextPressHandler | undefined): {
  onPress: SelectableTextPressHandler | undefined;
  onPressIn?: SelectableTextPressHandler;
} {
  const pressInTimestampRef = useRef<number | undefined>(undefined);
  if (Platform.OS === "web") {
    return { onPress };
  }
  return {
    onPress: (event) => {
      if (!isHoldRelease(pressInTimestampRef.current, event?.nativeEvent.timestamp)) {
        onPress?.(event);
      }
    },
    onPressIn: (event) => {
      pressInTimestampRef.current = event?.nativeEvent.timestamp;
    },
  };
}

/** Testing Library's `fireEvent.press` calls press handlers without an event. */
type SelectableTextPressHandler = (event?: GestureResponderEvent) => void;
